/**
 * The county storm report, as a single self-contained page.
 *
 * A partner forwards this. That is the whole design brief, and it decides three
 * things that would otherwise look like omissions:
 *
 *   * **No company name and no logo**, for the same reason the storm history
 *     report has none (ADR 0043): its value is that it reads as a compilation
 *     of government records rather than a sales piece. A realtor forwarding a
 *     roofer's advertisement to twelve clients does it once. A realtor
 *     forwarding "here's what the Weather Service recorded in our county" does
 *     it every time.
 *   * **County-level, not per-address.** The recipient is a dozen clients
 *     scattered across Shelby, and a report about one house is not
 *     forwardable. Every report location is listed so a reader can find their
 *     own street.
 *   * **Every row carries its source.** Same rule as the adjuster's document:
 *     an alert links to the Weather Service's own record and every Local Storm
 *     Report names who filed it. A reader who checks one row and finds it
 *     stands is a reader who believes the rest.
 *
 * Pinned light. It is printed and forwarded, and a page that arrives dark
 * because the reader's phone is in dark mode prints as a black rectangle.
 */

import {
  hailSizeWords,
  weekdayName,
  WATCHED_COUNTIES,
  type StormTrigger,
  type WatchedCounty,
} from "./partnerStorms.js";

/** What the report page is given: one storm_events row, as stored. */
export interface CountyStormSnapshot {
  county: WatchedCounty;
  event_date: string;
  triggers: StormTrigger[];
  max_hail_in: string | number | null;
  max_wind_mph: string | number | null;
  rain_in_24h: string | number | null;
  summary: string | null;
  raw: {
    alerts?: {
      id: string;
      event: string;
      effective: string;
      ends: string | null;
      headline: string | null;
    }[];
    lsrs?: {
      valid: string;
      type: string;
      magnitude: number | null;
      city: string;
      source: string;
      remark: string;
      lat: number | null;
      lon: number | null;
    }[];
    station?: {
      station: string;
      precipIn: number | null;
      peakGustKt: number | null;
    } | null;
  };
}

const escape = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const num = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * `YYYYMMDDHHMM` in UTC, as a Central clock time.
 *
 * The reader is in Alabama and remembers the storm by when it was dark. A
 * table of UTC timestamps would be technically correct and useless — and,
 * worse, would put the whole evening on the following date.
 */
function centralTime(valid: string): string {
  const match = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})$/.exec(valid.trim());
  if (!match) return valid;
  const [, year, month, day, hour, minute] = match;
  const at = new Date(`${year}-${month}-${day}T${hour}:${minute}:00Z`);
  if (Number.isNaN(at.getTime())) return valid;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Chicago",
    hour: "numeric",
    minute: "2-digit",
  }).format(at);
}

function longDate(eventDate: string): string {
  const at = new Date(`${eventDate}T12:00:00Z`);
  if (Number.isNaN(at.getTime())) return eventDate;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Chicago",
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(at);
}

const TYPE_WORDS: Record<string, string> = {
  HAIL: "Hail",
  "TSTM WND GST": "Thunderstorm wind gust",
  "TSTM WND DMG": "Thunderstorm wind damage",
  "NON-TSTM WND GST": "Wind gust",
  "NON-TSTM WND DMG": "Wind damage",
  "HIGH WIND": "High wind",
  "HEAVY RAIN": "Heavy rain",
  "FLASH FLOOD": "Flash flood",
  FLOOD: "Flood",
};

/**
 * What a report measured, in the units its own type uses.
 *
 * Hail is inches, wind is miles per hour, and a report with no number on it
 * says so rather than showing a blank — the Weather Service logs wind damage
 * because something broke, not because anybody held an anemometer, and "not
 * measured" is the honest column value.
 */
function magnitudeWords(type: string, magnitude: number | null): string {
  if (magnitude === null) return "Not measured";
  if (type === "HAIL") {
    const words = hailSizeWords(magnitude);
    return `${magnitude.toFixed(2)}″${words ? ` (${words})` : ""}`;
  }
  return `${Math.round(magnitude)} mph`;
}

const TRIGGER_WORDS: Record<StormTrigger, string> = {
  hail: "Hail",
  wind: "Damaging wind",
  heavy_rain: "Heavy rain",
  flood_warning: "Flood warning",
};

/**
 * The whole page.
 *
 * One string, no external assets, no script. It is opened from a text message
 * on a phone with two bars of signal, and every byte it does not have to fetch
 * is a second it does not spend blank.
 */
export function renderCountyStormReport(snapshot: CountyStormSnapshot): string {
  const county =
    WATCHED_COUNTIES.find((row) => row.key === snapshot.county)?.label ??
    "The county";
  const station = snapshot.raw.station ?? null;
  const reports = [...(snapshot.raw.lsrs ?? [])].sort((a, b) =>
    a.valid.localeCompare(b.valid),
  );
  const alerts = snapshot.raw.alerts ?? [];

  const hail = num(snapshot.max_hail_in);
  const wind = num(snapshot.max_wind_mph);
  const rain = num(snapshot.rain_in_24h);

  const towns = [
    ...new Set(
      reports
        .filter((row) => row.type === "HAIL" && row.city)
        .map((row) => row.city),
    ),
  ];

  const title = `${county} — ${longDate(snapshot.event_date)}`;

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escape(title)}</title>
<style>
  :root {
    color-scheme: light;
    --ink: #1b2540;
    --navy: #16233d;
    --muted: #6b7688;
    --line: #e3e8f0;
    --bg: #f4f6fa;
    --card: #ffffff;
    --accent: #2f6fed;
    --accent-soft: #eaf1fe;
  }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    background: var(--bg);
    color: var(--ink);
    font: 16px/1.55 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    -webkit-text-size-adjust: 100%;
  }
  .wrap { max-width: 780px; margin: 0 auto; padding: 28px 18px 60px; }
  header { margin-bottom: 22px; }
  h1 { font-size: 27px; line-height: 1.2; margin: 0 0 6px; color: var(--navy); }
  .date { color: var(--muted); font-size: 15px; }
  .lede {
    background: var(--card);
    border: 1px solid var(--line);
    border-left: 4px solid var(--accent);
    border-radius: 12px;
    padding: 16px 18px;
    font-size: 18px;
    margin: 18px 0 22px;
  }
  h2 {
    font-size: 15px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--muted);
    margin: 26px 0 10px;
  }
  .figures { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 10px; }
  .fig { background: var(--card); border: 1px solid var(--line); border-radius: 12px; padding: 12px 14px; }
  .fig .k { display: block; font-size: 12px; text-transform: uppercase; letter-spacing: 0.07em; color: var(--muted); }
  .fig b { display: block; font-size: 24px; color: var(--navy); font-variant-numeric: tabular-nums; }
  .tags { display: flex; flex-wrap: wrap; gap: 6px; margin: 10px 0 0; }
  .tag { background: var(--accent-soft); color: #1d4ed8; border-radius: 20px; padding: 3px 11px; font-size: 13.5px; font-weight: 600; }
  .card { background: var(--card); border: 1px solid var(--line); border-radius: 12px; overflow: hidden; }
  table { width: 100%; border-collapse: collapse; font-size: 14.5px; }
  th, td { text-align: left; padding: 10px 12px; border-bottom: 1px solid var(--line); vertical-align: top; }
  th { font-size: 12px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--muted); background: #f7f9fc; }
  tr:last-child td { border-bottom: 0; }
  .scroll { overflow-x: auto; }
  .remark { color: var(--muted); font-size: 13.5px; }
  .empty { padding: 18px; color: var(--muted); }
  p { margin: 10px 0; }
  a { color: var(--accent); }
  footer { margin-top: 30px; padding-top: 16px; border-top: 1px solid var(--line); color: var(--muted); font-size: 13.5px; }
  @media print {
    body { background: #fff; }
    .wrap { max-width: none; padding: 0; }
    .card, .fig, .lede { break-inside: avoid; }
  }
</style>
</head>
<body>
<div class="wrap">

<header>
  <h1>${escape(county)}</h1>
  <div class="date">${escape(longDate(snapshot.event_date))}</div>
</header>

${snapshot.summary ? `<div class="lede">${escape(snapshot.summary)}</div>` : ""}

<h2>What was recorded</h2>
<div class="figures">
  <div class="fig"><span class="k">Largest hail</span><b>${
    hail === null ? "—" : `${hail.toFixed(2)}″`
  }</b></div>
  <div class="fig"><span class="k">Strongest wind</span><b>${
    wind === null ? "—" : `${Math.round(wind)} mph`
  }</b></div>
  <div class="fig"><span class="k">Rain in 24h</span><b>${
    rain === null ? "—" : `${rain.toFixed(2)}″`
  }</b></div>
  <div class="fig"><span class="k">Ground reports</span><b>${reports.length}</b></div>
</div>
<div class="tags">
  ${snapshot.triggers
    .map(
      (trigger) => `<span class="tag">${escape(TRIGGER_WORDS[trigger])}</span>`,
    )
    .join("\n  ")}
</div>

${
  towns.length > 0
    ? `<h2>Where hail was reported</h2>
<p>${escape(towns.join(", "))}. Each of these is a place somebody reported hail on the ground — see the table below for the size and the time.</p>`
    : ""
}

<h2>Warnings in force</h2>
${
  alerts.length === 0
    ? `<div class="card"><div class="empty">No warnings covered ${escape(
        county,
      )} on this date.</div></div>`
    : `<div class="card"><div class="scroll"><table>
<thead><tr><th>Product</th><th>From</th><th>Until</th></tr></thead>
<tbody>
${alerts
  .map(
    (alert) => `<tr>
  <td>${escape(alert.event)}${
    alert.headline ? `<div class="remark">${escape(alert.headline)}</div>` : ""
  }</td>
  <td>${escape(alert.effective ? centralIso(alert.effective) : "—")}</td>
  <td>${escape(alert.ends ? centralIso(alert.ends) : "—")}</td>
</tr>`,
  )
  .join("\n")}
</tbody></table></div></div>`
}

<h2>Every ground report</h2>
${
  reports.length === 0
    ? `<div class="card"><div class="empty">No Local Storm Reports were filed in ${escape(
        county,
      )} on this date.</div></div>`
    : `<div class="card"><div class="scroll"><table>
<thead><tr><th>Time</th><th>Place</th><th>What</th><th>Measurement</th><th>Reported by</th></tr></thead>
<tbody>
${reports
  .map(
    (row) => `<tr>
  <td>${escape(centralTime(row.valid))}</td>
  <td>${escape(row.city || "—")}</td>
  <td>${escape(TYPE_WORDS[row.type] ?? row.type)}</td>
  <td>${escape(magnitudeWords(row.type, row.magnitude))}</td>
  <td>${escape(row.source || "—")}${
    row.remark ? `<div class="remark">${escape(row.remark)}</div>` : ""
  }</td>
</tr>`,
  )
  .join("\n")}
</tbody></table></div></div>`
}

${
  station
    ? `<h2>Measured at ${escape(station.station)}</h2>
<div class="card"><div class="scroll"><table>
<thead><tr><th>Station</th><th>Rain, 24 hours</th><th>Peak gust</th></tr></thead>
<tbody><tr>
  <td>${escape(station.station)}</td>
  <td>${station.precipIn === null ? "Not reported" : `${station.precipIn.toFixed(2)}″`}</td>
  <td>${
    station.peakGustKt === null
      ? "Not reported"
      : `${Math.round(station.peakGustKt * 1.15078)} mph (${station.peakGustKt.toFixed(
          0,
        )} kt)`
  }</td>
</tr></tbody></table></div></div>`
    : ""
}

<h2>Where this came from</h2>
<p>
  Warnings are from the National Weather Service
  (<a href="https://api.weather.gov/alerts">api.weather.gov</a>). Ground
  reports are Local Storm Reports, filed by trained spotters, emergency
  managers, law enforcement and the public, and served by the Iowa
  Environmental Mesonet
  (<a href="https://mesonet.agron.iastate.edu/lsr/">mesonet.agron.iastate.edu</a>).
  Station measurements are the automated observations at the county airport.
</p>
<p>
  Local Storm Reports are preliminary and are revised in quality control. This
  page is the record as it stood when it was compiled; a later query may return
  slightly different rows.
</p>

<footer>
  ${escape(county)} · ${escape(longDate(snapshot.event_date))} · compiled from
  public National Weather Service records.
</footer>

</div>
</body>
</html>`;
}

/** An ISO instant as a Central date and time, for the warnings table. */
function centralIso(value: string): string {
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Chicago",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(at);
}

/** The tab title and the text a partner sees when they forward the link. */
export function countyReportTitle(snapshot: CountyStormSnapshot): string {
  const county =
    WATCHED_COUNTIES.find((row) => row.key === snapshot.county)?.label ??
    "County";
  const day = weekdayName(snapshot.event_date);
  return `${county} storm report — ${day || snapshot.event_date}`;
}
